import { useQuery } from "@tanstack/react-query";
import { PatientInquiryControllerApi } from "../../../services";
import { getConfigObject } from "../../../utils/utils";

/** Shared key so any inquiry action can invalidate the badge count. */
export const PATIENT_INQUIRY_STATUS_COUNTS_KEY =
  "patient-inquiry-status-counts";

/**
 * Keeps the badge close to real time without a socket: a new inquiry raised by
 * another user (or another tab) lands within this window, and any action taken
 * in this tab invalidates the key for an immediate refresh.
 */
const STATUS_COUNTS_POLL_MS = 30_000;

/** Counts are keyed by the inquiry status enum, only the shipped keys appear. */
type PatientInquiryStatusCounts = Partial<
  Record<"PENDING" | "CONFIRMED" | "REJECTED", number>
>;

interface UseGetPatientInquiryStatusCountsProps {
  xTENANTID?: string;
  enabled?: boolean;
}

/** Counts are never negative, and a missing/garbage key reads as zero. */
const toCount = (value: number | undefined) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : 0;
};

/**
 * Status counts for the practice the logged-in user belongs to (the tenant
 * header scopes it), used by the pending-inquiry badge in the scheduling
 * toolbar.
 *
 * On failure the counts read as zero so the caller can degrade quietly rather
 * than surface an error or a stale value.
 */
export const useGetPatientInquiryStatusCounts = ({
  xTENANTID,
  enabled = true,
}: UseGetPatientInquiryStatusCountsProps) => {
  const query = useQuery({
    queryKey: [PATIENT_INQUIRY_STATUS_COUNTS_KEY, xTENANTID],
    enabled,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: STATUS_COUNTS_POLL_MS,
    queryFn: async () =>
      await new PatientInquiryControllerApi(getConfigObject())
        .getPatientInquiryStatusCounts(xTENANTID)
        .then((res) => res?.data),
  });

  /** The generated `Response.data` is `object`, so the shape is named here. */
  const statusCounts = query.data?.data as
    PatientInquiryStatusCounts | undefined;

  return {
    ...query,
    statusCounts,
    pendingCount: toCount(statusCounts?.PENDING),
  };
};
